import { useMemo } from "react";
import { useStore } from "@/provider/store";
import { useDashboardMetrics } from "@/components/daylight-insight/api/useDashboardMetrics";
import { useDashboardPerfsContext } from "@/components/daylight-insight/api/DashboardPerfsProvider";
import { toMetricCategories } from "@/components/daylight-insight/api/metricsNormalize";
import type {
  DashboardMetricCategory,
  DashboardMetricsParams,
} from "@/components/daylight-insight/api/types";

import { useDashboardFilterSync } from "./useDashboardFilterSync";

interface PillarMetrics {
  category: DashboardMetricCategory | null;
  loading: boolean;
  error: string | null;
  /** The pillar's dashboard id — the SP's @DashboardID, reused by the
   *  By-Branch (branch-details) call. Null until the config resolves. */
  dashboardID: number | string | null;
}

/** Server-side filter selections. Changing any of these re-fires the request. */
export interface PillarFilters {
  /** Selected values keyed by the config filter `key` — the SP's `@Filters`.
   *  Null (or no key with a selection) = no filter. */
  filters?: Record<string, string[]> | null;
  /** False while the selection comes from a drill Focus — the SP then does not
   *  save it as the user's filter preference. Defaults to true. */
  saveUserFilterPreferences?: boolean;
}

/**
 * The id of the dashboard granted to a pillar — the SP's `@DashboardID`, and
 * the scope every one of the page's calls is keyed on (metrics, branch details,
 * saved views and saved filters alike). Null until `dashboard/config` resolves.
 */
export function useDashboardIdForPillar(pillarK: string): number | string | null {
  const { specFor } = useStore();
  return useMemo(() => specFor(pillarK)?.dashboardId ?? null, [specFor, pillarK]);
}

/**
 * Fetches the metric values for the dashboard behind a pillar and returns the
 * matching category. One fetch feeds the whole Operating View page (KPI cards,
 * more-metrics, and the By-Branch table), keyed off the selected dashboard,
 * service line, date range, and the config-driven filter selections — so a
 * filter change refetches server-side rather than filtering rows on the client.
 */
export function useDashboardMetricsForPillar(
  pillarK: string,
  filters: PillarFilters = {},
  enabled = true,
): PillarMetrics {
  const s = useStore();

  // The dashboard granted to this pillar — its id is the SP's @DashboardID.
  const dashboardID = useDashboardIdForPillar(pillarK);

  // `enabled` false holds the request: the page passes false until the saved
  // filters (`dashboard/custom-views`) have been read and applied, so the first
  // metrics call already carries them rather than firing with the defaults and
  // immediately re-firing.
  //
  // Memoized over the individual fields rather than rebuilt each render: the
  // caller passes a fresh `filters` object every time, and `useDashboardFilterSync`
  // reads this object as its notion of "which cache entry are we on". A new
  // identity per render would have it re-deciding that on every render.
  // Compared by content: the caller rebuilds the map every render.
  const filtersKey = JSON.stringify(filters.filters ?? null);
  const params = useMemo<DashboardMetricsParams | null>(
    () =>
      enabled && dashboardID != null && s.serviceLineSystemID
        ? {
            dashboardID,
            serviceLineSystemID: s.serviceLineSystemID,
            startDate: s.range.start,
            endDate: s.range.end,
            includePriorPeriod: true,
            filters: JSON.parse(filtersKey) as Record<string, string[]> | null,
            saveUserFilterPreferences: filters.saveUserFilterPreferences ?? true,
          }
        : null,
    [
      enabled,
      dashboardID,
      s.serviceLineSystemID,
      s.range.start,
      s.range.end,
      filtersKey,
      filters.saveUserFilterPreferences,
    ],
  );

  const { data, loading, error } = useDashboardMetrics(params);

  // Persist the filter selection when — and only when — this response came from
  // cache, so the metrics SP (which normally saves it as a side effect of
  // running) did not. See `useDashboardFilterSync` for why that case exists.
  const perfs = useDashboardPerfsContext();
  useDashboardFilterSync({
    params,
    fetching: loading,
    hasData: data != null,
    savedFilters: perfs.filters,
    // `enabled` is the page's `filtersApplied`, which only flips once the saved
    // row has been read AND applied to the controls — exactly the point from
    // which the controls, not the server, are the source of truth.
    ready: enabled && perfs.ready,
  });

  // Render EVERY metric the API returns — the frontend does NOT filter by
  // `MetricCategory`. The SP decides which metrics belong to this dashboard, so
  // all returned categories are merged into one and shown verbatim (a metric
  // whose category differs from the current dashboard is still displayed).
  const category = useMemo<DashboardMetricCategory | null>(() => {
    const categories = toMetricCategories(data);
    if (!categories.length) return null;
    return {
      category: categories[0].category,
      metrics: categories.flatMap((c) => c.metrics),
    };
  }, [data]);

  return { category, loading, error, dashboardID };
}
